import axios from 'axios';

import { webSocketApiUrlsDictionary } from '../constants';

const theme = localStorage.getItem('theme');

const defaultUrl = webSocketApiUrlsDictionary[theme]?.gatewayUrl || process.env.REACT_APP_API_GATEWAY_URL;

const BASE_URL = `${defaultUrl}/mk-ms-virtualgroup`;

type Headers = {
  Authorization?: string;
  Platform: string
};

const defaultHeaders: Headers = {
  Platform: 'DASHBOARD'
};

// Lido a cada chamada: no carregamento do modulo o login pode ainda nao ter gravado o tenant
const getTenantId = () => localStorage.getItem('api-key');

const offsetInMinutes = new Date().getTimezoneOffset();

const timezoneOffset = -(offsetInMinutes / 60);

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    ...defaultHeaders,
    'X-Timezone-Offset': timezoneOffset
  },
});

// Lido a cada chamada, como o tenant: o login nao recarrega a pagina, e trocar de usuario sem F5
// mandaria o token (e a autoria na auditoria) do usuario anterior.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth-token');
  const headers = { ...config.headers };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    delete headers.Authorization;
  }
  return { ...config, headers };
});

export const getVirtualGroups = (page: number = 1, size: number = 10, search?: string) => {
  try {
    const params = { page: --page, size, tenant_id: getTenantId(), search: search || null };

    return api.get('/virtual-groups/list', { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const createVirtualGroup = (virtualGroup) => {
  try {
    const response = api.post('/virtual-groups', { ...virtualGroup, tenant_id: getTenantId() });

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getVirtualGroupsChargeboxes = (page: number = 1, size: number = 10, search?: string | undefined) => {
  try {
    const params = { page: --page, size, tenant_id: getTenantId(), search: search || null };

    return api.get('/charge-boxs', { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getVirtualGroup = (id: string) => {
  try {
    const params = { tenant_id: getTenantId() };

    return api.get(`/virtual-groups/${id}`, { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const updateVirtualGroup = (virtualGroupId, data) => {
  try {
    return api.put(`/virtual-groups/${virtualGroupId}`, { ...data, tenant_id: getTenantId() });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getVirtualGroupEvents = (id: string, page: number, size: number) => {
  try {
    const params = { page: --page, size };

    return api.get(`/virtual-groups/${id}/events`, { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const deleteVirtualGroupChargebox = (virtualGroupChargeboxId) => {
  try {
    return api.delete(`/virtual-groups/charge-boxs/${virtualGroupChargeboxId}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getVirtualGroupTransactions = (id: string, page: number, size: number) => {
  try {
    const params = { page: --page, size };

    return api.get(`/virtual-groups/${id}/transactions`, { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getVirtualGroupChargerboxes = (virtualGroupId: string, page: number = 1, size: number = 10) => {
  try {
    const params = { virtual_group_id: virtualGroupId, page: --page, size };

    return api.get('/virtual-groups/charge-boxs/list', { params });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

/**
 * MV2-1958: todas as estacoes do grupo, percorrendo as paginas. Ativar o smart charging com so a
 * primeira pagina (10) deixava as demais estacoes do grupo sem limite.
 */
export const getAllVirtualGroupChargerboxes = async (virtualGroupId: string, pageSize: number = 100) => {
  const { data: first } = await getVirtualGroupChargerboxes(virtualGroupId, 1, pageSize);
  const content = [...(first?.content ?? [])];
  const totalPages = first?.totalPages ?? 1;

  for (let page = 2; page <= totalPages; page += 1) {
    const { data } = await getVirtualGroupChargerboxes(virtualGroupId, page, pageSize);
    content.push(...(data?.content ?? []));
  }

  return { ...first, content };
};

export const getVirtualGroupSmartCharging = (id: string) => {
  try {
    return api.get(`/virtual-groups/smart-charging/${id}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getVirtualGroupDynamicPricing = (id: string) => {
  try {
    return api.get(`/virtual-groups/dynamic-pricing/${id}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const createVirtualGroupSmartCharging = (data: any) => {
  try {
    return api.post('/virtual-groups/smart-charging', { ...data });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const getVirtualGroupChargebox = (chargeBoxId: string) => {
  try {
    return api.get(`/charge-boxs/${chargeBoxId}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const deleteVirtualGroup = (virtualGroupId) => {
  try {
    return api.delete(`/virtual-groups/${virtualGroupId}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const createVirtualGroupChargeboxsAll = (chargeboxs) => {
  try {
    return api.post('/virtual-groups/charge-boxs/all', chargeboxs);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const createVirtualGroupDynamicPricing = (data) => {
  try {
    return api.post('/virtual-groups/dynamic-pricing', { ...data });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};

export const updateVirtualGroupDynamicPricing = (id, data) => {
  try {
    return api.put(`/virtual-groups/dynamic-pricing/${id}`, { ...data });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw error.response;
    } else {
      throw error;
    }
  }
};
